import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { brandStyle, PoweredBy } from "@/lib/branding";
import { Lockup } from "@/components/Brand";

// Public participant page for a comprehension check: anonymous, group-only, no pass/fail. Reveals the right answer + a short note after submitting.
export default function ComprehensionPage() {
  const { token } = useParams();
  useSeo({ title: "Quick check", description: "A few questions about a change at your workplace. Anonymous, no sign-in.", path: "/check" });
  const doneKey = `changeos_check_done_${token.slice(0, 12)}`;
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [answers, setAnswers] = useState({});
  const [reveal, setReveal] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/public/comprehension/${encodeURIComponent(token)}`).then((r) => { setData(r.data); setState(r.data.valid ? "form" : "invalid"); }).catch(() => setState("invalid"));
  }, [token]); // eslint-disable-line
  const brand = data?.branding;
  const missing = data?.questions ? data.questions.filter((q) => answers[q.id] === undefined) : [];
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (missing.length) { setErr(`Please answer ${missing.length === 1 ? "the last question" : `all questions — ${missing.length} left`}.`); document.getElementById(`q-${missing[0].id}`)?.focus(); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/comprehension/${encodeURIComponent(token)}`, { answers });
      if (r.accepted) { localStorage.setItem(doneKey, "1"); setReveal(r.reveal); setState("done"); } else setState("invalid");
    } catch (ex) { setErr(errMsg(ex)); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-background text-foreground" style={brandStyle(brand)} data-testid="check-page" data-branded={brand ? "true" : "false"}>
      <a href="#check-main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-card focus:px-3 focus:py-2 focus:rounded-lg focus:text-sm focus:font-bold">Skip to questions</a>
      <header className="max-w-md mx-auto px-4 pt-6 pb-3 flex items-center justify-between gap-3">
        {brand ? (
          <div className="flex items-center gap-2 min-w-0 border-l-4 border-brand-raw pl-3" data-testid="check-practice-brand">
            {brand.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt="" className="h-7 object-contain" />}
            <span className="text-sm font-bold truncate">{brand.display_name}</span>
          </div>
        ) : <Lockup height={20} bar="var(--brand-teal)" />}
        {brand && <PoweredBy />}
      </header>
      <main id="check-main" className="max-w-md mx-auto px-4 pb-16" aria-live="polite">
        {state === "loading" && <p className="text-sm text-muted-foreground py-10 text-center" data-testid="check-loading">Loading…</p>}
        {state === "invalid" && (
          <section className="pp-card p-6 mt-4" data-testid="check-invalid">
            <h1 className="font-head text-2xl font-extrabold">This link isn't available</h1>
            <p className="text-base text-subtle mt-3 leading-relaxed">It may have been used already, or the check it belonged to has closed. If you were given a personal code, each one works once.</p>
            <p className="text-sm text-muted-foreground mt-4">Nothing about you was recorded by opening it.</p>
          </section>
        )}
        {state === "done" && reveal && (
          <section data-testid="check-done">
            <div className="pp-card p-6 mt-4 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center" aria-hidden="true"><Check size={24} /></div>
              <h1 className="font-head text-2xl font-extrabold mt-4">Thank you</h1>
              <p className="text-base text-subtle mt-2 leading-relaxed">Your answers have been added to your group's total. Nobody can see which answers were yours — and there's no score to pass or fail. Here's what each one was about.</p>
            </div>
            <ol className="mt-5 space-y-3 list-none p-0">
              {reveal.map((r, i) => (
                <li key={r.id} className="pp-card p-4" data-testid={`check-reveal-${i}`}>
                  <p className="text-base font-bold leading-snug">{r.text}</p>
                  <div className="mt-3 space-y-1.5">
                    {r.options.map((opt, oi) => {
                      const isCorrect = oi === r.correct_index;
                      const isYours = oi === r.your_index;
                      return (
                        <div key={oi} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isCorrect ? "border-success-300 bg-success-50 text-success-900" : isYours ? "border-danger-200 bg-danger-50 text-danger-800" : "border-border text-subtle"}`} data-testid={`check-reveal-${i}-opt-${oi}`}>
                          {isCorrect ? <Check size={15} className="shrink-0" aria-label="Correct answer" /> : isYours ? <X size={15} className="shrink-0" aria-label="Your answer" /> : <span className="w-[15px] shrink-0" aria-hidden="true" />}
                          <span>{opt}</span>
                          {isYours && <span className="ml-auto text-[11px] font-bold uppercase tracking-wide">Your answer</span>}
                        </div>
                      );
                    })}
                  </div>
                  {r.explanation && <p className="text-sm text-subtle mt-3 leading-relaxed" data-testid={`check-reveal-${i}-explain`}>{r.explanation}</p>}
                </li>
              ))}
            </ol>
          </section>
        )}
        {state === "form" && data && (
          <form onSubmit={submit} noValidate data-testid="check-form">
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">A few quick questions</h1>
            {data.context?.title && <p className="text-base text-subtle mt-2" data-testid="check-context">About <span className="font-bold text-foreground">{data.context.title}</span>{data.context.group && <> · for <span className="font-bold text-foreground">{data.context.group}</span></>}</p>}
            <section className="pp-card p-4 mt-4 bg-muted/40" data-testid="check-before">
              <h2 className="text-sm font-bold">{data.how_it_works.heading}</h2>
              <ul className="list-disc pl-5 mt-2 space-y-1.5 text-sm text-subtle leading-relaxed">
                {data.how_it_works.points.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </section>
            <ol className="mt-6 space-y-6 list-none p-0">
              {data.questions.map((q, i) => (
                <li key={q.id}>
                  <fieldset className="pp-card p-4" data-testid={`check-q-${q.id}`} id={`q-${q.id}`} tabIndex={-1}>
                    <legend className="sr-only">Question {i + 1} of {data.questions.length}: {q.text}</legend>
                    <div aria-hidden="true"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{i + 1} of {data.questions.length}</div><p className="text-base font-bold mt-1 leading-snug">{q.text}</p></div>
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className="relative flex items-center min-h-[48px] rounded-xl border border-border bg-card px-4 text-base font-semibold cursor-pointer transition-colors has-[:checked]:bg-primary has-[:checked]:text-primary-foreground has-[:checked]:border-primary hover:border-primary focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                          <input type="radio" name={q.id} value={oi} checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} data-testid={`check-${q.id}-${oi}`} className="sr-only" />
                          <span>{opt}</span>
                          {answers[q.id] === oi && <Check size={18} className="ml-auto" aria-hidden="true" />}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </li>
              ))}
            </ol>
            {err && <div role="alert" className="mt-4 rounded-xl bg-danger-50 border border-danger-200 text-danger-800 text-sm px-4 py-3" data-testid="check-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="check-submit" className="pp-btn-dark w-full mt-6 min-h-[52px] text-base">{busy ? "Sending…" : "Send my answers"}</button>
            <details className="mt-8 rounded-2xl border border-border-soft bg-muted/40 p-4 text-sm" data-testid="check-how">
              <summary className="font-bold cursor-pointer min-h-[44px] flex items-center">What is and isn't recorded</summary>
              <div className="mt-3 space-y-1 text-subtle leading-relaxed">
                <div className="font-bold text-foreground">What is not stored</div>
                <ul className="list-disc pl-5 mt-1 space-y-1">{data.how_it_works.not_stored.map((s) => <li key={s}>{s}</li>)}</ul>
                <p className="pt-2">Results are shown to the people leading the change as group totals only, once at least {data.floor} people in a group have answered.</p>
              </div>
            </details>
          </form>
        )}
      </main>
    </div>
  );
}
